import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, PenLine } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TagEditor } from "@/components/TagEditor";

const FieldBlock = ({ name, label, changed, reason, edited, keyword, children }) => (
  <div data-testid={`diff-field-${name}`} className="border-t py-5 first:border-t-0">
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="font-sans text-sm font-semibold uppercase tracking-wider">{label}</h3>
      {changed ? <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase text-accent-foreground">Changed</span>
        : <span className="text-xs text-muted-foreground">No change</span>}
      {keyword && <span data-testid={`diff-keywords-label-${name}`} className="rounded-full border border-[#E9C98B]/40 bg-[#E9C98B]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#F3DDAE]">Your keywords</span>}
      {edited && <span className="inline-flex items-center gap-1 text-xs text-primary"><PenLine className="h-3 w-3" />Edited by you</span>}
    </div>
    {changed && reason && (
      <p data-testid={`diff-reason-${name}`} className="mt-1.5 flex items-start gap-1.5 text-sm text-muted-foreground">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />{reason}
      </p>
    )}
    <div className="mt-3 grid gap-3 lg:grid-cols-2">{children}</div>
  </div>
);

const Old = ({ children, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-secondary/60 p-3 text-sm text-muted-foreground">
    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider">Current</p>{children}
  </div>
);
const New = ({ children }) => (
  <div className="rounded-xl border border-primary/30 glass p-3 text-sm">
    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Proposed</p>{children}
  </div>
);

export const FixDiff = ({ proposal, options, shielded, onPushed, onDiscard }) => {
  const [p, setP] = useState(proposal.proposed);
  const [val, setVal] = useState(proposal.validation);
  const [edited, setEdited] = useState(proposal.edited_fields);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pushError, setPushError] = useState("");
  const o = proposal.original;
  const kw = proposal.keyword_fields || [];
  useEffect(() => { setP(proposal.proposed); setVal(proposal.validation); setEdited(proposal.edited_fields); }, [proposal]);

  const update = (k, v) => { setP((x) => ({ ...x, [k]: v })); setDirty(true); };
  const save = async (patch = p) => {
    const { data } = await api.patch(`/proposals/${proposal.id}`, patch);
    setVal(data.validation);
    setEdited(data.edited_fields);
    setDirty(false);
    return data;
  };
  const saveField = (k, v) => save({ [k]: v ?? p[k] }).catch((e) => toast.error(errMsg(e)));

  const push = async () => {
    setBusy(true);
    setPushError("");
    try {
      if (dirty) await save();
      await api.post(`/proposals/${proposal.id}/push`, { approved: true });
      toast.success("Pushed. Backup saved to version history.");
      onPushed();
    } catch (e) {
      const msg = errMsg(e, "Push failed — nothing was changed.");
      setPushError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const changed = (k) => JSON.stringify(o[k]) !== JSON.stringify(p[k]);
  const attrKeys = Object.keys(o.attributes || {});

  return (
    <section data-testid="ai-fix-diff" className="hairline-gold rounded-2xl border glass p-5 sm:p-6 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-2xl font-medium">Proposed fix</h2>
        <span className="text-xs text-muted-foreground">Nothing is pushed until you click "Approve & push".</span>
      </div>
      <FieldBlock name="title" label="Title" changed={changed("title")} reason={proposal.reasons.title} edited={edited.includes("title")} keyword={kw.includes("title")}>
        <Old testid="diff-old-title">{o.title}</Old>
        <New>
          <Input value={p.title} onChange={(e) => update("title", e.target.value)} onBlur={() => saveField("title")} data-testid="proposal-title-input" />
          <p className={`mt-1 font-mono text-xs ${p.title.length > 140 ? "text-destructive" : "text-muted-foreground"}`} data-testid="proposal-title-count">{p.title.length}/140</p>
        </New>
      </FieldBlock>
      <FieldBlock name="tags" label="Tags" changed={changed("tags")} reason={proposal.reasons.tags} edited={edited.includes("tags")} keyword={kw.includes("tags")}>
        <Old testid="diff-old-tags">
          <div className="flex flex-wrap gap-1">{o.tags.map((t, i) => <span key={i} className="rounded-full border px-2 py-0.5 text-xs">{t}</span>)}</div>
          <p className="mt-1 font-mono text-xs">{o.tags.length}/13 tags</p>
        </Old>
        <New><TagEditor tags={p.tags} onChange={(t) => { update("tags", t); saveField("tags", t); }} /></New>
      </FieldBlock>
      <FieldBlock name="attributes" label="Attributes" changed={changed("attributes")} reason={proposal.reasons.attributes} edited={edited.includes("attributes")}>
        <Old testid="diff-old-attributes">
          {attrKeys.map((k) => <p key={k}><span className="text-foreground/70">{k}:</span> {o.attributes[k] || "—"}</p>)}
        </Old>
        <New>
          <div className="grid gap-2">
            {attrKeys.map((k) => (
              <div key={k} className="grid grid-cols-[110px_1fr] items-center gap-2">
                <span className="text-xs text-muted-foreground truncate">{k}</span>
                <Select value={p.attributes[k] || "__none"} onValueChange={(v) => { const a = { ...p.attributes, [k]: v === "__none" ? null : v }; update("attributes", a); saveField("attributes", a); }}>
                  <SelectTrigger className={`h-8 text-xs ${o.attributes[k] !== p.attributes[k] ? "border-primary" : ""}`} data-testid={`proposal-attr-${k.replace(/\s+/g, "-").toLowerCase()}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Not set</SelectItem>
                    {(options[k] || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </New>
      </FieldBlock>
      <FieldBlock name="description" label="Description" changed={changed("description")} reason={proposal.reasons.description} edited={edited.includes("description")} keyword={kw.includes("description")}>
        <Old testid="diff-old-description"><p className="whitespace-pre-line max-h-72 overflow-auto">{o.description}</p></Old>
        <New>
          <Textarea value={p.description} rows={10} onChange={(e) => update("description", e.target.value)} onBlur={() => saveField("description")} data-testid="proposal-description-input" />
          <p className="mt-1 font-mono text-xs text-muted-foreground">{p.description.length} characters</p>
        </New>
      </FieldBlock>
      {(!val.title_ok || !val.tags_ok) && (
        <div data-testid="proposal-validation-errors" className="mb-4 rounded-xl border border-rose-300/25 bg-rose-400/10 p-3 text-sm text-rose-200">
          <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" />Etsy rule check</p>
          <ul className="mt-1 list-disc pl-5">{[...val.title_errors, ...val.tag_errors].map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      {pushError && <p data-testid="push-error-message" className="mb-3 text-sm font-medium text-destructive">{pushError}</p>}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-5">
        {shielded && <span className="mr-auto text-sm text-[#E9C98B]" data-testid="push-disabled-shield-note">Protected bestseller — unlock to push.</span>}
        <Button variant="ghost" className="rounded-full" onClick={onDiscard} data-testid="discard-proposal-button">Discard</Button>
        <Button className="btn-aurora rounded-full px-6" onClick={push} disabled={busy || shielded} data-testid="approve-and-push-button">
          {busy ? "Pushing…" : "Approve & push"}
        </Button>
      </div>
    </section>
  );
};
